/**
 * Transaction API Hooks
 *
 * React Query hooks for transaction-related API operations. Mutation hooks
 * take entity ids as mutation variables, never at hook creation (WEBD-18),
 * and invalidate through the shared map (WEBD-07).
 */

import type { Transaction, CreateTransactionDto } from '@luminaria/shared';
import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { invalidate } from '../api/invalidation';
import { transactionKeys } from '../api/queryKeys';
import {
  transactionsApi,
  type ResolveCorporateActionInput,
  type TransactionListParams,
  type UpdateTransactionInput,
} from '../api/transactionsApi';

// Re-export types for convenience
export type { Transaction } from '@luminaria/shared';

/**
 * Fetch transactions for an account
 */
export function useTransactionsByAccount(
  accountId: string,
  params?: TransactionListParams,
  options?: Omit<UseQueryOptions<Transaction[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery<Transaction[]>({
    queryKey: transactionKeys.byAccount(accountId, params),
    queryFn: () => transactionsApi.listByAccount(accountId, params),
    enabled: !!accountId,
    ...options,
  });
}

/**
 * Fetch all transactions for a portfolio
 */
export function useTransactionsByPortfolio(
  portfolioId: string,
  params?: TransactionListParams,
  options?: Omit<UseQueryOptions<Transaction[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery<Transaction[]>({
    queryKey: transactionKeys.byPortfolio(portfolioId, params),
    queryFn: () => transactionsApi.listByPortfolio(portfolioId, params),
    enabled: !!portfolioId,
    ...options,
  });
}

/**
 * Fetch a single transaction by ID
 */
export function useTransaction(
  id: string,
  options?: Omit<UseQueryOptions<Transaction>, 'queryKey' | 'queryFn'>
) {
  return useQuery<Transaction>({
    queryKey: transactionKeys.detail(id),
    queryFn: () => transactionsApi.get(id),
    enabled: !!id,
    ...options,
  });
}

/**
 * Create a new transaction in `dto.accountId`
 */
export function useCreateTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateTransactionDto) => transactionsApi.create(dto.accountId, dto),
    onSuccess: () => { void invalidate(queryClient, 'transactions'); },
  });
}

interface UpdateTransactionVariables extends UpdateTransactionInput {
  id: string;
}

/**
 * Update an existing transaction. `null` clears an optional field.
 */
export function useUpdateTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...updates }: UpdateTransactionVariables) => transactionsApi.update(id, updates),
    onSuccess: (updatedTransaction) => {
      queryClient.setQueryData<Transaction>(transactionKeys.detail(updatedTransaction.id), updatedTransaction);
      void invalidate(queryClient, 'transactions');
    },
  });
}

/**
 * Delete a transaction
 */
export function useDeleteTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await transactionsApi.remove(id);
      return id;
    },
    onSuccess: (deletedId) => {
      queryClient.removeQueries({ queryKey: transactionKeys.detail(deletedId) });
      void invalidate(queryClient, 'transactions');
    },
  });
}

/**
 * Fetch split/merger/spinoff transactions currently awaiting user resolution.
 * Drives the CostBasis page's review list and count badge.
 */
export function useCorporateActionsNeedingReview(
  options?: Omit<UseQueryOptions<Transaction[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery<Transaction[]>({
    queryKey: transactionKeys.corporateActionsNeedingReview(),
    queryFn: () => transactionsApi.corporateActionsNeedingReview(),
    staleTime: 60 * 1000,
    ...options,
  });
}

/**
 * Resolve (or ignore) a split/merger/spinoff transaction. The backend
 * re-triggers lot recalculation, so every ledger-derived query refetches.
 */
export function useResolveCorporateAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ResolveCorporateActionInput) => transactionsApi.resolveCorporateAction(input),
    onSuccess: () => { void invalidate(queryClient, 'transactions'); },
  });
}
